import { allocatePayments, type BuildContext } from '@ims/core'
import type { CustomerLedger, Repository } from '@ims/data'
import { paidAheadOf } from './statement.ts'

/**
 * A customer's payment set against their open bills, oldest first (`open` in the order to
 * pay them, as `reports.openInvoices` returns them): each bill gets what it still owes
 * until the money runs out. Money beyond what they owe is set against nothing: it is paid
 * ahead, and `planPaidAhead` sets it against the next bill.
 */
export function allocateOldestFirst(
  open: readonly { invoiceId: string; balance: number }[],
  amountPaisa: number,
): { invoiceId: string; amountPaisa: number }[] {
  const out: { invoiceId: string; amountPaisa: number }[] = []
  let left = amountPaisa
  for (const bill of open) {
    if (left <= 0) break
    if (bill.balance <= 0) continue
    const share = Math.min(bill.balance, left)
    out.push({ invoiceId: bill.invoiceId, amountPaisa: share })
    left -= share
  }
  return out
}

/**
 * Money a customer paid ahead set against their open bills: the oldest payment with money
 * left first, the oldest bill first. Never more than a bill still owes.
 */
export function planPaidAhead(
  ledger: CustomerLedger,
): { paymentId: string; invoiceId: string; amountPaisa: number }[] {
  const bills = ledger.bills
    .map((b) => ({ id: b.id, date: b.date, open: b.totalPaisa - b.paidPaisa - b.creditedPaisa }))
    .filter((b) => b.open > 0)
    .sort((a, b) => a.date.localeCompare(b.date))
  const payments = ledger.payments
    .map((p) => ({ id: p.id, date: p.date, left: paidAheadOf(p) }))
    .filter((p) => p.left > 0)
    .sort((a, b) => a.date.localeCompare(b.date))
  const out: { paymentId: string; invoiceId: string; amountPaisa: number }[] = []
  for (const p of payments) {
    for (const b of bills) {
      if (p.left <= 0) break
      if (b.open <= 0) continue
      const share = Math.min(p.left, b.open)
      out.push({ paymentId: p.id, invoiceId: b.id, amountPaisa: share })
      p.left -= share
      b.open -= share
    }
  }
  return out
}

/**
 * Sets a customer's paid-ahead money against their open bills, as its own commit: run it
 * after the commit of a bill, never inside it, so a refusal (another device used the same
 * money first) never costs the sale. Returns how much was set; 0 when there was nothing to do.
 */
export async function settlePaidAhead(
  repo: Pick<Repository, 'commit' | 'parties'>,
  ctx: BuildContext,
  customerId: string | null | undefined,
): Promise<number> {
  if (!customerId || !repo.parties) return 0
  const plan = planPaidAhead(await repo.parties.customerLedger(customerId))
  if (plan.length === 0) return 0
  await repo.commit(allocatePayments(ctx, plan).ops)
  return plan.reduce((sum, a) => sum + a.amountPaisa, 0)
}
